/**
 * «نرخ‌ها و سلامت منابع» (Phase 6; PRODUCT_REQUIREMENTS §9.1). Honest provider health (healthy / degraded
 * / no data / disabled), the current snapshot per pair (median, emergency or unavailable — with used and
 * excluded sources), emergency rates (reason + expiry ≤ 24 h, revoke-only) and per-route disable flags.
 * Approved Iranian fiat sources are owner input (L3): sources here are admin data, never assumed.
 */
import {
  AppContainer,
  Badge,
  Button,
  Card,
  DataTable,
  formatDecimal,
  PageHeader,
  SelectField,
  StateView,
  TextField,
} from '@hopex/ui';
import { useState } from 'react';
import { api, dt } from '../../api.ts';
import { CommandDialog, Facts } from '../../lib/command.tsx';
import { Remote, useRemote } from '../../lib/remote.tsx';

interface Overview {
  params: { staleSeconds: number; outlierRatio: string };
  sources: {
    id: string;
    kind: string;
    provider: string;
    label: string;
    enabled: boolean;
    health: string;
    lastError: string | null;
    lastSuccessAt: string | null;
    version: string;
  }[];
  snapshots: {
    pair: string;
    rate: string | null;
    method: string;
    healthy: boolean;
    reason: string | null;
    sourcesUsed: unknown;
    excluded: unknown;
    at: string | null;
  }[];
  emergencies: {
    id: string;
    pair: string;
    rate: string;
    reason: string;
    createdAt: string;
    expiresAt: string;
    active: boolean;
  }[];
  routeFlags: { route: string; disabled: boolean; reason: string }[];
}

const HEALTH: Record<
  string,
  { label: string; tone: 'positive' | 'negative' | 'accent' | 'neutral' }
> = {
  HEALTHY: { label: 'سالم', tone: 'positive' },
  DEGRADED: { label: 'ناپایدار', tone: 'negative' },
  NO_DATA: { label: 'بدون داده', tone: 'accent' },
  DISABLED: { label: 'غیرفعال', tone: 'neutral' },
};
const METHOD: Record<string, { label: string; tone: 'positive' | 'negative' | 'accent' }> = {
  MEDIAN: { label: 'میانه‌ی منابع', tone: 'positive' },
  EMERGENCY: { label: 'نرخ اضطراری', tone: 'accent' },
  NIGHT: { label: 'نرخ شبانه (ثابت)', tone: 'accent' },
  UNAVAILABLE: { label: 'در دسترس نیست', tone: 'negative' },
};

/**
 * Display only: «60320.000000000000000000» → «60,320». Digits are regrouped and trailing zeros
 * trimmed as text — no rounding, no truncation, no number conversion; the server value stays exact.
 */
function showRate(rate: string | null): string {
  if (rate === null) return '—';
  try {
    return formatDecimal(rate, { trimTrailingZeros: true });
  } catch {
    return rate;
  }
}

type Open =
  | { k: 'source' }
  | { k: 'toggle'; s: Overview['sources'][number] }
  | { k: 'emergency'; pair: string }
  | { k: 'revoke'; id: string; pair: string }
  | { k: 'route' };

export function RatesPage() {
  const r = useRemote<Overview>('/rates');
  const [open, setOpen] = useState<Open | null>(null);
  const [f, setF] = useState({
    kind: 'CRYPTO',
    provider: '',
    label: '',
    rate: '',
    minutes: '60',
    route: '',
    disabled: true,
  });
  const done = () => (setOpen(null), r.reload());
  return (
    <AppContainer>
      <PageHeader
        title="نرخ‌ها و سلامت منابع"
        actions={
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onPress={() => setOpen({ k: 'route' })}>
              توقف / ادامه‌ی مسیر
            </Button>
            <Button size="sm" variant="secondary" onPress={() => setOpen({ k: 'source' })}>
              منبع جدید
            </Button>
          </div>
        }
      />
      <Remote r={r}>
        {(d) => (
          <div className="flex flex-col gap-4" data-rates>
            <p className="m-0 text-[12.5px] text-fg-muted">
              منبع کهنه: بیش از {d.params.staleSeconds.toLocaleString('fa-IR')} ثانیه · حذف داده‌ی
              پرت: بیش از {d.params.outlierRatio} از میانه · نرخ هوپکس = میانه‌ی دست‌کم دو منبع سالم
            </p>
            <Card className="p-5" data-rate-snapshots>
              <h2 className="m-0 mb-3 text-[15px] font-bold">نرخ جاری هر جفت</h2>
              <DataTable<Overview['snapshots'][number] & { id: string }>
                label="نرخ‌های جاری"
                rows={d.snapshots.map((s) => ({ ...s, id: s.pair }))}
                density="compact"
                columns={[
                  {
                    id: 'pair',
                    header: 'جفت',
                    isRowHeader: true,
                    cell: (s) => (
                      <bdi dir="ltr" className="font-mono">
                        {s.pair}
                      </bdi>
                    ),
                  },
                  {
                    id: 'rate',
                    header: 'نرخ',
                    numeric: true,
                    cell: (s) => (
                      <bdi dir="ltr" className="font-mono">
                        {showRate(s.rate)}
                      </bdi>
                    ),
                  },
                  {
                    id: 'm',
                    header: 'روش',
                    cell: (s) => (
                      <Badge tone={METHOD[s.method]?.tone ?? 'negative'}>
                        {METHOD[s.method]?.label ?? s.method}
                      </Badge>
                    ),
                  },
                  {
                    id: 'src',
                    header: 'منابع',
                    cell: (s) => (
                      <span className="text-[12px]">
                        {(s.sourcesUsed as { provider: string }[])
                          .map((x) => x.provider)
                          .join('، ') || '—'}
                        {(s.excluded as unknown[]).length
                          ? ` · حذف: ${(s.excluded as { provider: string }[]).map((x) => x.provider).join('، ')}`
                          : ''}
                      </span>
                    ),
                  },
                  {
                    id: 'at',
                    header: 'زمان',
                    cell: (s) => (
                      <span className="text-[12px]">
                        {s.at ? dt(s.at) : '—'}
                        {s.reason && !s.healthy ? ` · ${s.reason}` : ''}
                      </span>
                    ),
                  },
                  {
                    id: 'act',
                    header: 'اضطراری',
                    cell: (s) => (
                      <Button
                        size="sm"
                        variant="ghost"
                        onPress={() => setOpen({ k: 'emergency', pair: s.pair })}
                      >
                        تنظیم
                      </Button>
                    ),
                  },
                ]}
                empty={<StateView compact kind="empty" title="جفتی فعال نیست" />}
              />
            </Card>
            <Card className="p-5" data-rate-sources>
              <h2 className="m-0 mb-3 text-[15px] font-bold">منابع نرخ</h2>
              {d.sources.length === 0 ? (
                <StateView
                  compact
                  kind="empty"
                  title="منبعی تعریف نشده است"
                  description="بدون منبع سالم، همه‌ی مسیرهای خرید، فروش و تبدیل متوقف می‌مانند."
                />
              ) : (
                <ul className="m-0 grid list-none gap-1.5 p-0 text-[13px]">
                  {d.sources.map((s) => (
                    <li
                      key={s.id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-line px-3 py-2"
                    >
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold">{s.label}</span>
                        <bdi dir="ltr" className="font-mono text-[12px] text-fg-muted">
                          {s.kind} · {s.provider}
                        </bdi>
                        <Badge tone={HEALTH[s.health]?.tone ?? 'neutral'}>
                          {HEALTH[s.health]?.label ?? s.health}
                        </Badge>
                        {s.lastSuccessAt && (
                          <span className="text-fg-subtle">آخرین موفق: {dt(s.lastSuccessAt)}</span>
                        )}
                      </span>
                      <Button size="sm" variant="ghost" onPress={() => setOpen({ k: 'toggle', s })}>
                        {s.enabled ? 'غیرفعال' : 'فعال'}
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            <Card className="p-5" data-rate-emergencies>
              <h2 className="m-0 mb-3 text-[15px] font-bold">نرخ‌های اضطراری</h2>
              {d.emergencies.length === 0 ? (
                <StateView compact kind="empty" title="نرخ اضطراری ثبت نشده است" />
              ) : (
                <ul className="m-0 grid list-none gap-1.5 p-0 text-[13px]">
                  {d.emergencies.map((e) => (
                    <li
                      key={e.id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-line px-3 py-2"
                    >
                      <span>
                        <bdi dir="ltr" className="font-mono">
                          {e.pair} = {showRate(e.rate)}
                        </bdi>{' '}
                        · {e.reason} · تا {dt(e.expiresAt)}{' '}
                        <Badge tone={e.active ? 'accent' : 'neutral'}>
                          {e.active ? 'فعال' : 'منقضی/لغو'}
                        </Badge>
                      </span>
                      {e.active && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onPress={() => setOpen({ k: 'revoke', id: e.id, pair: e.pair })}
                        >
                          لغو
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {d.routeFlags.length > 0 && (
                <p className="m-0 mt-3 text-[12.5px] text-fg-muted">
                  مسیرهای متوقف:{' '}
                  {d.routeFlags
                    .filter((x) => x.disabled)
                    .map((x) => x.route)
                    .join('، ') || 'ندارد'}
                </p>
              )}
            </Card>
          </div>
        )}
      </Remote>
      {open?.k === 'source' && (
        <CommandDialog
          title="منبع نرخ جدید"
          canSubmit={/^[a-z0-9_-]{2,40}$/.test(f.provider) && f.label.trim().length >= 2}
          run={async (reason) =>
            void (await api.post('/rates/sources', {
              kind: f.kind,
              provider: f.provider,
              label: f.label,
              reason,
            }))
          }
          onClose={() => setOpen(null)}
          onDone={done}
        >
          <p className="m-0 text-[12.5px] text-fg-muted">
            منبع غیرفعال ساخته می‌شود. منابع ریالی باید تأییدشده‌ی مالک باشند (L3).
          </p>
          <SelectField
            label="نوع"
            value={f.kind}
            onChange={(v) => setF({ ...f, kind: v })}
            options={[
              { value: 'CRYPTO', label: 'بازار رمزارز (جفت با تتر)' },
              { value: 'FIAT', label: 'نرخ تتر به تومان' },
            ]}
          />
          <TextField
            label="کلید ارائه‌دهنده"
            value={f.provider}
            onChange={(v) => setF({ ...f, provider: v.toLowerCase() })}
            ltr
          />
          <TextField
            label="نام نمایشی"
            value={f.label}
            onChange={(v) => setF({ ...f, label: v })}
          />
        </CommandDialog>
      )}
      {open?.k === 'toggle' && (
        <CommandDialog
          title={`${open.s.enabled ? 'غیرفعال' : 'فعال'} کردن ${open.s.label}`}
          tone="caution"
          run={async (reason) =>
            void (await api.post(`/rates/sources/${open.s.id}`, {
              enabled: !open.s.enabled,
              expectedVersion: open.s.version,
              reason,
            }))
          }
          onClose={() => setOpen(null)}
          onDone={done}
        />
      )}
      {open?.k === 'emergency' && (
        <CommandDialog
          title={`نرخ اضطراری ${open.pair}`}
          tone="caution"
          canSubmit={/^\d+(\.\d+)?$/.test(f.rate) && /^\d+$/.test(f.minutes)}
          run={async (reason) =>
            void (await api.post('/rates/emergency', {
              pair: open.pair,
              rate: f.rate,
              expiresInMinutes: Number.parseInt(f.minutes, 10),
              reason,
            }))
          }
          onClose={() => setOpen(null)}
          onDone={done}
        >
          <Facts
            rows={[
              [
                'اثر',
                'فقط وقتی کمتر از دو منبع سالم باشد استفاده می‌شود و با انقضا خودکار کنار می‌رود.',
              ],
            ]}
          />
          <TextField label="نرخ" value={f.rate} onChange={(v) => setF({ ...f, rate: v })} ltr />
          <TextField
            label="مدت اعتبار (دقیقه، حداکثر ۱۴۴۰)"
            value={f.minutes}
            onChange={(v) => setF({ ...f, minutes: v })}
            ltr
          />
        </CommandDialog>
      )}
      {open?.k === 'revoke' && (
        <CommandDialog
          title={`لغو نرخ اضطراری ${open.pair}`}
          tone="danger"
          run={async (reason) =>
            void (await api.post(`/rates/emergency/${open.id}/revoke`, { reason }))
          }
          onClose={() => setOpen(null)}
          onDone={done}
        />
      )}
      {open?.k === 'route' && (
        <CommandDialog
          title="توقف یا ادامه‌ی یک مسیر"
          tone="caution"
          canSubmit={/^(BUY|SELL|USDT_TO_COIN|COIN_TO_USDT):[A-Z0-9]{2,12}$/.test(f.route)}
          run={async (reason) =>
            void (await api.post('/rates/routes', { route: f.route, disabled: f.disabled, reason }))
          }
          onClose={() => setOpen(null)}
          onDone={done}
        >
          <TextField
            label="مسیر (مثلاً BUY:BTC)"
            value={f.route}
            onChange={(v) => setF({ ...f, route: v.toUpperCase() })}
            ltr
          />
          <label className="flex items-center gap-2 text-[13px]">
            <input
              type="checkbox"
              checked={f.disabled}
              onChange={(e) => setF({ ...f, disabled: e.target.checked })}
            />
            متوقف شود
          </label>
        </CommandDialog>
      )}
    </AppContainer>
  );
}
